/**
 * Release 26: the move from the old address to the new one, as the band (shell/PitaPindahAlamat)
 * and the save guard (./db) decide it. DOM-free, so the node tests import it.
 *
 * "Pindah sekarang" is enabled only when nothing can be lost: the outbox is empty, the device is
 * online, and the last sync succeeded. Pressed, it records the move first, so no save can slip
 * into the outbox during the final sync; then it runs that sync and, with the outbox still
 * empty, opens Masuk on the new address. Anything left unsent takes the record back and the
 * device works on where it is. The old origin's data stays where it is: a safety copy until
 * the cleanup release.
 */
import { format, uiStrings } from '@satu/shared'
import type { SyncState, WorkerError } from '../db/protocol'
import {
  ALAMAT_BARU,
  batalPindah,
  type PenyimpananAlamat,
  sudahPindah,
  tandaiPindah,
  urlPindah,
} from './alamat'
import { isConnected } from './store/sync-view'

const teks = uiStrings.pindahAlamat

export type KeadaanPindah =
  | { boleh: true }
  | {
      boleh: false
      /** The one reason, in the band under the button. */
      alasan: string
      /** "Sinkronkan sekarang" helps: something waits, or the last run failed. */
      sinkronkan: boolean
    }

export function keadaanPindah(sync: SyncState, online: boolean): KeadaanPindah {
  if (sync.pending > 0) {
    return {
      boleh: false,
      alasan: format(teks.menunggu, { jumlah: sync.pending }),
      sinkronkan: true,
    }
  }
  if (!isConnected(sync, online)) {
    return { boleh: false, alasan: teks.perluKoneksi, sinkronkan: false }
  }
  if (sync.lastSyncAt === null || sync.lastError !== null) {
    return { boleh: false, alasan: teks.sinkronkanDulu, sinkronkan: true }
  }
  return { boleh: true }
}

/** The band's sentence and the refused save's reason once the device has moved. */
export function teksSudahPindah(alamat: string = ALAMAT_BARU): string {
  return format(teks.sudahPindah, { alamat })
}

/**
 * The guard every save passes (./db, db/client `tolakTulis`): a device that moved saves nothing
 * more on the old address. The record is read at every save, so the move takes effect at once.
 */
export function tolakTulisPindah(storage: PenyimpananAlamat | null): WorkerError | null {
  if (!sudahPindah(storage)) return null
  return { code: 'alamat_pindah', message: teksSudahPindah() }
}

export interface DepsPindah {
  storage: PenyimpananAlamat | null
  /** One sync run now (the worker's `sync:now`), answering the state after it. */
  sinkronkan(): Promise<SyncState>
  /** Leaves for the new address. */
  buka(url: string): void
  /** ISO time of the move, kept with the record. */
  sekarang(): string
  /** The page's protocol (`https:`): the new address is opened the same way. */
  protocol: string
}

/** True when the page is leaving for the new address; false when the device stays. */
export async function jalankanPindah(deps: DepsPindah): Promise<boolean> {
  // First the record: from here every save is refused, so the final run sends the last of it.
  if (!tandaiPindah(deps.storage, deps.sekarang())) return false
  let hasil: SyncState | null = null
  try {
    hasil = await deps.sinkronkan()
  } catch {
    hasil = null
  }
  if (hasil === null || hasil.pending > 0 || hasil.lastError !== null || !hasil.connected) {
    batalPindah(deps.storage)
    return false
  }
  deps.buka(urlPindah(deps.protocol))
  return true
}
